if (!customElements.get("inspiration-section")) {
  class InspirationSection extends HTMLElement {
    constructor() {
      super();

      this.media = window.matchMedia("(min-width: 1200px)");
      this.media.addEventListener("change", () => this.onResize());
      this.swiper = undefined;
      this.container = this.querySelector(".swiper-container");
      this.scrollbar = this.querySelector(".swiper-scrollbar");
      this.onResize();

      // klatki sa w dokumencie dwa razy: raz w kaflu desktopowym, raz w duzym
      // zdjeciu mobilnym. Liczymy je osobno, zeby licznik nie podwoil wartosci.
      this.frames = Array.from(this.querySelectorAll("[data-inspiration-stage] [data-inspiration-frame]"));
      this.mobileFrames = Array.from(
        this.querySelectorAll("[data-inspiration-mobile-stage] [data-inspiration-frame]"),
      );
      this.dots = Array.from(this.querySelectorAll("[data-inspiration-dot]"));
      /* Karty produktow kazdego zestawu. Zestaw bazowy ma numer 0, kolejne to
         bloki sekcji. Gdy sekcja nie ma blokow, jest tylko grupa 0 i nic sie
         nie przelacza - dokladnie tak, jak dzialalo przed wprowadzeniem
         zestawow. */
      this.setSlides = Array.from(this.querySelectorAll(".swiper-slide[data-inspiration-set]"));
      this.hasSets = new Set(this.setSlides.map((slide) => slide.dataset.inspirationSet)).size > 1;
      this.counter = this.querySelector("[data-inspiration-counter]");
      this.prevArrow = this.querySelector("[data-inspiration-prev]");
      this.nextArrow = this.querySelector("[data-inspiration-next]");
      this.frameIndex = 0;

      this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
      this.autoplayTimer = undefined;
      this.autoplayStopped = false;
      /* Kursor nad duzym zdjeciem. Osobna flaga, bo `pauseAutoplay()` tylko
         gasi biezacy licznik, a wznowic potrafi kilka niezaleznych zdarzen
         (obserwator widocznosci, `focusout`, powrot do karty). Bez niej
         przewiniecie do sekcji z kursorem juz na zdjeciu wznawialo podmiane
         klatek - zgloszenie 8.09.2026: "jak jestem myszka na zdjeciu, to nie
         powinno mi podmieniac zdjecia". */
      this.pointerNadKadrem = false;

      if (this.frames.length > 1) {
        if (this.prevArrow) {
          this.prevArrow.addEventListener("click", () => {
            this.stopAutoplay();
            this.showFrame(this.frameIndex - 1);
          });
        }
        if (this.nextArrow) {
          this.nextArrow.addEventListener("click", () => {
            this.stopAutoplay();
            this.showFrame(this.frameIndex + 1);
          });
        }
        this.dots.forEach((dot, i) => {
          dot.addEventListener("click", () => {
            this.stopAutoplay();
            this.showFrame(i);
          });
        });
        this.showFrame(0);
        this.setupAutoplay();
        this.setupPrefetch();
      }

      /* Punkty dzialaja takze przy jednej klatce (sekcja bez zestawow),
         wiec ich obsluga jest POZA warunkiem na liczbe klatek. */
      this.setupSpots();
      this.setupSwipe();
    }

    /* Zdjecia klatek maja `loading="lazy"`, wiec przegladarka pobiera je sama,
       gdy sekcja zbliza sie do ekranu. To wystarcza w typowym scrollu, ale nie
       gwarantuje niczego, gdy uzytkownik wejdzie w sekcje szybko albo trafi tu
       z odnosnika z kotwica - wtedy pierwsze przelaczenie i tak czekaloby na
       pobranie pliku. Dlatego przy pierwszym wejsciu sekcji w ekran zamieniamy
       pozostalym klatkom sposob wczytywania na natychmiastowy.

       Kolejnosc ma znaczenie: najpierw klatka NASTEPNA po widocznej, bo ta
       zostanie pokazana jako pierwsza. */
    setupPrefetch() {
      const warm = () => this.prefetchFrames();
      if (!("IntersectionObserver" in window)) {
        warm();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          warm();
        },
        { rootMargin: "200px 0px" },
      );
      observer.observe(this);
    }

    prefetchFrames() {
      /* Wersja mobilna i desktopowa klatek siedza w dokumencie obie, a ukryta
         ma `display: none` na calym kadrze. Dogrzewamy WYLACZNIE widoczny
         zestaw - inaczej ustawienie natychmiastowego wczytywania zmusiloby
         przegladarke do pobrania obu zestawow naraz. */
      const visible = (frame) => frame.offsetParent !== null;
      const sets = [this.frames, this.mobileFrames].filter((set) => set.some(visible));
      const order = [];
      sets.forEach((set) => {
        for (let step = 1; step <= set.length; step += 1) {
          order.push(set[(this.frameIndex + step) % set.length]);
        }
      });

      /* Klatki poza pierwsza trojka nie maja adresow w atrybutach - czekaja
         w `data-srcset` / `data-src` (patrz snippet 'inspiration-frame-image').
         Wpuszczamy je PO KOLEI: nastepna dopiero, gdy poprzednia sie wczyta,
         albo po sekundzie, gdy wczytywanie sie przeciaga. Kadr wazy okolo
         300 kB, a zestawow bywa kilkanascie - wpuszczenie wszystkich naraz
         zapycha lacze dokladnie w chwili, gdy uzytkownik doczytal do sekcji. */
      const kolejka = order.filter(Boolean);
      const dalej = (i) => {
        if (i >= kolejka.length) return;
        this.wpuscKlatke(kolejka[i]).then(() => dalej(i + 1));
      };
      dalej(0);
    }

    /* Przesuniecie palcem po duzym zdjeciu przelacza zestaw. Strzalki sa
       tylko w naglowku, a na telefonie kciuk siega zdjecia, nie naglowka.

       Kadr jest odnosnikiem do produktu, wiec trzeba rozroznic dotkniecie od
       przeciagniecia: klikniecie po gescie jest zatrzymywane w fazie
       przechwytywania, inaczej przesuniecie otwieraloby karte produktu.
       Ruch pionowy zostaje przewijaniem strony (`touch-action: pan-y`
       w arkuszu), wiec gest liczy sie dopiero, gdy poziomy przewaza. */
    setupSwipe() {
      const stage = this.querySelector("[data-inspiration-mobile-stage]");
      if (!stage || this.frames.length < 2) return;

      const PROG = 45;
      let startX = 0;
      let startY = 0;
      let gest = false;

      stage.addEventListener(
        "pointerdown",
        (event) => {
          if (event.pointerType === "mouse") return;
          startX = event.clientX;
          startY = event.clientY;
          gest = false;
        },
        { passive: true },
      );

      stage.addEventListener(
        "pointerup",
        (event) => {
          if (event.pointerType === "mouse") return;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (Math.abs(dx) < PROG || Math.abs(dx) < Math.abs(dy)) return;
          gest = true;
          this.stopAutoplay();
          const kolejna = dx < 0 ? this.frameIndex + 1 : this.frameIndex - 1;
          const ostatnia = this.frames.length - 1;
          this.showFrame(kolejna < 0 ? ostatnia : kolejna > ostatnia ? 0 : kolejna);
        },
        { passive: true },
      );

      stage.addEventListener(
        "click",
        (event) => {
          if (!gest) return;
          event.preventDefault();
          event.stopPropagation();
          gest = false;
        },
        true,
      );
    }

    /* Podstawia klatce prawdziwe adresy i oddaje obietnice spelniana po jej
       wczytaniu. Klatka bez odroczonych adresow (pierwsza trojka) tylko
       przelacza sie na natychmiastowe wczytywanie. */
    wpuscKlatke(frame) {
      return new Promise((resolve) => {
        if (!frame) {
          resolve();
          return;
        }
        const odroczone = [...frame.querySelectorAll("[data-srcset], [data-src]")];
        const imgs = [...frame.querySelectorAll("img")];
        if (!odroczone.length) {
          imgs.forEach((img) => {
            img.loading = "eager";
          });
          resolve();
          return;
        }

        let zostalo = imgs.length;
        const gotowe = () => {
          zostalo -= 1;
          if (zostalo <= 0) resolve();
        };
        imgs.forEach((img) => {
          if (img.complete && img.currentSrc) {
            gotowe();
            return;
          }
          img.addEventListener("load", gotowe, { once: true });
          img.addEventListener("error", gotowe, { once: true });
          img.loading = "eager";
        });
        setTimeout(resolve, 1000);

        odroczone.forEach((el) => {
          if (el.dataset.srcset) {
            el.srcset = el.dataset.srcset;
            delete el.dataset.srcset;
          }
          if (el.dataset.src) {
            el.src = el.dataset.src;
            delete el.dataset.src;
          }
        });
      });
    }

    setupAutoplay() {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (!this.autoplayDelay || reducedMotion.matches) return;

      // pauza tylko nad duzym zdjeciem: to link, a podmiana klatki pod kursorem
      // zmienilaby cel klikniecia. Najechanie na siatke produktow nie zatrzymuje.
      const stage = this.querySelector("[data-inspiration-stage]");
      if (stage) {
        stage.addEventListener("mouseenter", () => {
          this.pointerNadKadrem = true;
          this.pauseAutoplay();
        });
        stage.addEventListener("mouseleave", () => {
          this.pointerNadKadrem = false;
          this.resumeAutoplay();
        });
      }
      this.addEventListener("focusin", () => this.pauseAutoplay());
      this.addEventListener("focusout", () => this.resumeAutoplay());
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.pauseAutoplay();
        else this.resumeAutoplay();
      });

      if ("IntersectionObserver" in window) {
        // sekcja poza ekranem nie przewija klatek w tle
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.resumeAutoplay();
              else this.pauseAutoplay();
            });
          },
          { threshold: 0.25 },
        ).observe(this);
      } else {
        this.resumeAutoplay();
      }
    }

    resumeAutoplay() {
      /* Kursor nad zdjeciem ma pierwszenstwo nad kazdym powodem wznowienia. */
      if (this.pointerNadKadrem) return;
      if (this.autoplayStopped || !this.autoplayDelay || this.autoplayTimer !== undefined) return;
      this.autoplayTimer = window.setInterval(() => {
        const next = this.frameIndex + 1 > this.frames.length - 1 ? 0 : this.frameIndex + 1;
        this.showFrame(next);
      }, this.autoplayDelay);
    }

    pauseAutoplay() {
      if (this.autoplayTimer === undefined) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }

    stopAutoplay() {
      this.autoplayStopped = true;
      this.pauseAutoplay();
    }

    showFrame(index) {
      const last = this.frames.length - 1;
      if (index < 0 || index > last) return;

      this.frameIndex = index;
      const activate = (frame, i) => {
        frame.classList.toggle("is-active", i === index);
        // klatka poza widokiem nie moze byc celem taba ani czytnika ekranu
        frame.setAttribute("aria-hidden", i === index ? "false" : "true");
        frame.tabIndex = i === index ? 0 : -1;
      };
      this.frames.forEach(activate);
      this.mobileFrames.forEach(activate);
      this.dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === index);
        dot.setAttribute("aria-selected", i === index ? "true" : "false");
      });

      if (this.counter) {
        const current = String(index + 1).padStart(2, "0");
        const total = String(this.frames.length).padStart(2, "0");
        this.counter.textContent = `${current}/${total}`;
      }
      if (this.prevArrow) this.prevArrow.disabled = index === 0;
      if (this.nextArrow) this.nextArrow.disabled = index === last;

      /* Klatka wybrana strzalka albo kropka nie moze czekac na swoja kolej
         w kolejce dogrzewania - wpuszczamy ja (i nastepna) natychmiast. */
      [this.frames[index], this.mobileFrames[index], this.frames[index + 1], this.mobileFrames[index + 1]]
        .filter(Boolean)
        .forEach((frame) => this.wpuscKlatke(frame));

      this.showSpots(this.frames[index]?.dataset.inspirationSet);
      this.showSet(this.frames[index]?.dataset.inspirationSet);
    }

    /* Karty po prawej nalezace do innego zestawu sa zdejmowane z ukladu.
       Na mobile te same karty sa slajdami karuzeli, wiec po podmianie
       biblioteka musi przeliczyc szerokosci - bez `update()` zostaje
       przewijanie o dlugosc poprzedniego zestawu. */
    /* Punkty ida wlasna warstwa, rownolegle do kart po prawej: kazda warstwa
       niesie numer swojego zestawu, widoczna jest warstwa zestawu z klatki. */
    showSpots(setIndex) {
      if (setIndex === undefined) return;
      this.querySelectorAll("[data-inspiration-spots]").forEach((layer) => {
        const czyjaTo = layer.dataset.inspirationSpots === String(setIndex);
        layer.classList.toggle("is-active", czyjaTo);
        if (!czyjaTo) {
          layer.querySelectorAll(".is-open").forEach((spot) => spot.classList.remove("is-open"));
        }
      });
    }

    /* Na telefonie punkt otwiera sie DOTKNIECIEM, nie najechaniem.

       Warunek obejmuje teraz takze waskie okno, nie tylko `hover: none`:
       urzadzenia hybrydowe (tablet z klawiatura, laptop z ekranem dotykowym)
       zglaszaja `hover: hover` i obsluga kliku sie na nich nie wlaczala
       (zgloszenie 8.09.2026).

       Dotkniecie PRZELACZA karte. Wczesniej drugie dotkniecie prowadzilo do
       produktu, bo caly punkt byl odnosnikiem - od czasu, gdy karta ma wlasny
       odnosnik i przycisk koszyka, punkt jest zwyklym pojemnikiem i drugie
       dotkniecie nie mialo juz czego zrobic. */
    setupSpots() {
      const dotyk = window.matchMedia("(hover: none), (max-width: 991px)");
      this.addEventListener("click", (event) => {
        const spot = event.target.closest(".inspiration__spot");
        if (!spot || !dotyk.matches) return;
        /* klikniecie w sama karte (odnosnik, przycisk) ma dzialac normalnie */
        if (event.target.closest(".inspiration__spot-card")) return;
        event.preventDefault();
        const bylaOtwarta = spot.classList.contains("is-open");
        this.querySelectorAll(".inspiration__spot.is-open").forEach((inny) => {
          inny.classList.remove("is-open");
        });
        if (!bylaOtwarta) spot.classList.add("is-open");
      });
      document.addEventListener("click", (event) => {
        if (this.contains(event.target)) return;
        this.querySelectorAll(".inspiration__spot.is-open").forEach((spot) => spot.classList.remove("is-open"));
      });
    }

    showSet(setIndex) {
      if (!this.hasSets || setIndex === undefined) return;
      this.setSlides.forEach((slide) => {
        slide.classList.toggle(
          "inspiration__slide--hidden",
          slide.dataset.inspirationSet !== setIndex,
        );
      });
      if (this.swiper !== undefined) {
        this.swiper.update();
        this.swiper.slideTo(0, 0);
      }
    }

    onResize() {
      if (this.media.matches) {
        if (this.swiper !== undefined) {
          this.swiper.destroy();
          this.swiper = undefined;
        }
        return;
      }

      if (this.swiper === undefined) {
        this.swiper = new Swiper(this.container, {
          // Przewijanie touchpadem: dwa palce w poziomie ruszaja karuzela.
          // forceToAxis pilnuje, zeby ruch w pionie nadal przewijal strone,
          // releaseOnEdges oddaje gest stronie na koncu karuzeli.
          mousewheel: { enabled: true, forceToAxis: true, releaseOnEdges: true, thresholdDelta: 8 },
          on: {
            // karuzela powstaje dopiero ponizej 1200 px, wiec przy przejsciu
            // z desktopu musi zaczac od zestawu, ktory jest wlasnie wybrany
            afterInit: (swiper) => swiper.update(),
          },
          loop: false,
          grabCursor: false,
          speed: 500,
          spaceBetween: 8,
          slidesPerView: 2,
          watchSlidesProgress: true,
          scrollbar: { el: this.scrollbar, enabled: true },
          breakpoints: {
            768: { spaceBetween: 16, slidesPerView: 3 },
          },
        });
      }
    }
  }
  customElements.define("inspiration-section", InspirationSection);
}
